import { Card, CardContent, CardHeader, CardTitle } from '@ui/card.tsx';
import { Outlet, useNavigate } from '@tanstack/react-router';
import { useLocation } from '@tanstack/react-router';
import { AlertCircle, Check } from 'lucide-react';
import { useWorkspaceFormStore } from '@admin/context/workspace-form-context.tsx';
import type { WorkspaceWorkspaceWriteModel } from '@r/api/generated/client.schemas.ts';
import { useUser } from '@r/api/auth.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { useTranslation } from 'react-i18next';
import { cn } from '@r/utils/utils.ts';
import type { MouseEvent } from 'react';
import {
  useAllConfigs,
  createConfigsMap,
  getConfigsFromResponse
} from '@r/hooks/use-all-configs.ts';

type WorkspaceFormData = WorkspaceWorkspaceWriteModel;

const areConfigsValid = (
  requiredConfigs: { type: string; key: string }[],
  formData: Partial<WorkspaceFormData> | null,
  configsMap: Map<string, string> // Map de "type:key" vers config IRI
) => {
  if (!formData?.workspaceConfigs) return false;

  return requiredConfigs.every((config) => {
    const configKey = `${config.type}:${config.key}`;
    const configIri = configsMap.get(configKey);
    if (!configIri) return false;

    return formData.workspaceConfigs?.some(
      (workspaceConfig) => workspaceConfig.config === configIri && workspaceConfig.value
    );
  });
};

export const getStepValidation = (
  stepId: number,
  formData: Partial<WorkspaceFormData> | null,
  configsMap?: Map<string, string> // Map de "type:key" vers config IRI (optionnel pour compatibilité avec router)
) => {
  switch (stepId) {
    case 1: {
      // Si configsMap n'est pas disponible (dans le router), on vérifie juste que les configs existent
      if (!configsMap) {
        const logo = formData?.workspaceConfigs?.find(
          (config) => config.config?.includes('/configs/') && config.media
        );
        return Boolean(
          formData?.name &&
            formData?.domain &&
            logo?.value &&
            formData?.defaultHelpRecipient &&
            formData?.defaultEmailSender
        );
      }
      const logoConfigIri = configsMap.get('media:logo');
      const logo = logoConfigIri
        ? formData?.workspaceConfigs?.find((config) => config.config === logoConfigIri)
        : null;
      return Boolean(
        formData?.name &&
          formData?.domain &&
          logo?.value &&
          formData?.defaultHelpRecipient &&
          formData?.defaultEmailSender
      );
    }
    case 2: {
      // Si configsMap n'est pas disponible (dans le router), on retourne true pour ne pas bloquer
      if (!configsMap) {
        return true;
      }
      // Si configsMap est disponible, on vérifie que les configs existent dans formData
      const requiredConfigs = [
        { type: 'style', key: 'background' },
        { type: 'style', key: 'foreground' },
        { type: 'style', key: 'primary' },
        { type: 'style', key: 'primary-foreground' },
        { type: 'style', key: 'card' },
        { type: 'style', key: 'card-foreground' },
        { type: 'style', key: 'footer' }
      ];
      return areConfigsValid(requiredConfigs, formData, configsMap);
    }
    case 3:
      return Boolean(formData?.defaultEmailSender && formData.defaultHelpRecipient);
    case 4:
      return Boolean(formData?.currentLogicWorkspace?.steps?.length);
    default:
      return false;
  }
};

const VerticalBreadcrumb = () => {
  const { workspaces } = useWorkspaceFormStore();
  const { t } = useTranslation();
  const user = useUser();
  const activeWorkspace = user?.data
    ? getActiveWorkspaceUniversal(user?.data.visibleWorkspaces)
    : null;
  const activeWorkspaceId = activeWorkspace?.workspace.id;
  const formData = workspaces[activeWorkspaceId as number];
  const currentPath = useLocation().pathname;
  const currentStep = Number.parseInt(currentPath.split('/')[3], 10);

  // Fetch de toutes les configs disponibles (mutualisé avec les steps)
  const configsQuery = useAllConfigs();

  // Créer un mapping de "type:key" vers config IRI
  const configsMap = createConfigsMap(getConfigsFromResponse(configsQuery.data));

  const canAccessStep = (stepId: number) => {
    // Can always access current or previous steps
    if (stepId <= currentStep) return true;

    // For next steps, check if all previous steps are completed
    for (let i = 1; i < stepId; i++) {
      if (!getStepValidation(i, formData, configsMap)) return false;
    }

    // Can only access the immediate next step
    return stepId === currentStep + 1;
  };

  const items = [
    {
      id: 1,
      label: t('form.workspace.layout.params.label'),
      validation: () => getStepValidation(1, formData, configsMap)
    },
    {
      id: 2,
      label: t('form.workspace.layout.interface.label'),
      validation: () => getStepValidation(2, formData, configsMap)
    },
    {
      id: 3,
      label: t('form.workspace.layout.general.label'),
      validation: () => getStepValidation(3, formData, configsMap)
    },
    {
      id: 4,
      label: t('form.workspace.layout.legal.label'),
      validation: () => getStepValidation(4, formData, configsMap)
    }
  ];

  const navigate = useNavigate();

  const handleClick = (stepId: number) => (e: MouseEvent) => {
    const isAccessible = canAccessStep(stepId);
    const previousStepCompleted =
      stepId === 1 || getStepValidation(stepId - 1, formData, configsMap);

    if (!isAccessible || !previousStepCompleted) {
      e.preventDefault();
      return;
    }

    navigate({ to: `/create-workspace/step/${stepId}` });
  };

  return (
    <nav className="space-y-4">
      {items.map((item) => {
        const isStepValid = item.validation();
        const isPreviousStep = item.id < currentStep;
        const isCurrentStep = item.id === currentStep;
        const isAccessible = canAccessStep(item.id);
        const previousStepCompleted =
          item.id === 1 || getStepValidation(item.id - 1, formData, configsMap);

        return (
          <button
            key={item.id}
            type="button"
            className={`flex flex-col ${
              isAccessible && previousStepCompleted ? 'cursor-pointer' : 'cursor-not-allowed'
            }`}
            onClick={handleClick(item.id)}
          >
            <div className="flex items-center">
              <div
                className={cn('w-6 h-6 rounded-full flex items-center justify-center mr-3', {
                  'border border-purple-600 text-purple-600': isCurrentStep,
                  'bg-success text-white': isPreviousStep && isStepValid,
                  'bg-yellow-500 text-white': isPreviousStep && !isStepValid,
                  'bg-gray-200 text-gray-400': !isAccessible || !previousStepCompleted,
                  'border border-gray-200 text-gray-300':
                    isAccessible && previousStepCompleted && !isPreviousStep && !isCurrentStep
                })}
              >
                {isPreviousStep ? (
                  isStepValid ? (
                    <Check size={16} className="!m-0" />
                  ) : (
                    <AlertCircle size={16} className="!m-0" />
                  )
                ) : (
                  item.id
                )}
              </div>
              <div className="flex flex-col">
                <span
                  className={cn({
                    'text-purple-600 font-medium': isCurrentStep,
                    'text-gray-500 hover:text-purple-400': isPreviousStep,
                    'text-gray-400': !isAccessible || !previousStepCompleted,
                    'text-gray-300':
                      isAccessible && previousStepCompleted && !isPreviousStep && !isCurrentStep
                  })}
                >
                  {item.label}
                </span>
              </div>
            </div>
          </button>
        );
      })}
    </nav>
  );
};

export const CreateWorkspaceLayout = () => {
  return (
    <div className="flex overflow-auto p-4 min-h-screen bg-stone-200">
      <div className="mr-4 space-y-4 w-1/4">
        <Card className="w-full p-3 pb-[10rem]">
          <CardHeader>
            {/* TODO add fiddy logo */}
            <CardTitle>Fiddy</CardTitle>
          </CardHeader>

          <CardContent className="p-0">
            <div className="p-4">
              <VerticalBreadcrumb />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-1 justify-center space-y-4">
        <div className="justify-center items-center w-full max-w-2xl">
          <Outlet />
        </div>
      </div>
    </div>
  );
};
